/**
 * GovLoginPage — Portal institucional AgentUS Gestão Pública.
 *
 * v0.99 · Fev/2026 · Redesign completo aderente ao site público
 * agentus.com.br/gov (estética dark institucional, logomarca oficial,
 * "Portal GOV." como marca) + reuso do padrão cinemático do login
 * privado (showcase à esquerda + form glass à direita).
 *
 * Fluxo de auth NÃO mudou — usa o mesmo AuthContext.login().
 * Backend intocado. Apenas cosmético/UX.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import GovAuthShowcase from "@/components/auth/GovAuthShowcase";

const ORG_TYPES = [
  { value: "gov_municipal",   label: "Prefeitura Municipal" },
  { value: "gov_secretariat", label: "Secretaria Municipal" },
  { value: "gov_chamber",     label: "Câmara Municipal" },
  { value: "gov_state",       label: "Órgão Estadual" },
];

const REQUEST_TYPES = [
  { value: "demo",         label: "Solicitar Demonstração" },
  { value: "proposta",     label: "Solicitar Proposta" },
  { value: "apresentacao", label: "Agendar Apresentação" },
];

export default function GovLoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [view, setView] = useState("login"); // "login" | "request"

  // v0.99.8 · Fev/2026 — Se o usuário chegou aqui já logado com tenant
  // GOV (ex: veio de outro tab), pula o form e vai direto pro painel.
  // Se está logado mas NÃO é GOV, deixa o form pra ele mudar de conta.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!auth?.user) return;
      try {
        const me = await api.get("/sector/me");
        if (!cancelled && me?.data?.is_gov) {
          navigate("/gov/painel", { replace: true });
        }
      } catch { /* silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [auth?.user, navigate]);

  return (
    <div
      className="relative min-h-screen text-white flex overflow-hidden"
      style={{ backgroundColor: "#04060B" }}
      data-testid="gov-login-page"
    >
      {/* Esquerda — showcase institucional (desktop lg+) */}
      <GovAuthShowcase />

      {/* Direita — auth surface */}
      <div className="relative flex-1 lg:basis-1/2 lg:grow-0 lg:shrink-0 min-h-screen flex flex-col overflow-hidden">
        {/* Ambient glow atrás do form */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(620px 480px at 50% 42%, rgba(52,168,83,0.06), transparent 65%), radial-gradient(700px 420px at 50% 115%, rgba(255,255,255,0.03), transparent 70%)",
          }}
        />
        {/* Grid pontilhado sutil */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
            backgroundSize: "34px 34px",
          }}
        />

        <header className="relative z-10 px-5 sm:px-7 pt-5 sm:pt-6 pb-1 flex items-center justify-between">
          {/* Logo real (só mobile — no desktop aparece no showcase esquerdo) */}
          <img
            src="/logo-white.png"
            alt="AgentUS Gestão Pública"
            className="h-[15px] sm:h-[16px] w-auto select-none opacity-95 lg:hidden"
            draggable="false"
            data-testid="gov-login-logo"
          />
          <span className="hidden lg:block" />
          <a
            href="https://www.agentus.com.br/gov"
            className="text-[10.5px] tracking-tight text-white/30 hover:text-white/80 transition-colors duration-200"
            data-testid="gov-login-back-to-site"
          >
            Site institucional →
          </a>
        </header>

        <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-2 sm:py-4">
          <div className="w-full max-w-[420px]">
            {view === "login" ? (
              <LoginView
                navigate={navigate}
                auth={auth}
                onRequest={() => setView("request")}
              />
            ) : (
              <RequestView onBack={() => setView("login")} />
            )}
          </div>
        </main>

        <footer className="relative z-10 px-6 py-4 text-center">
          <p className="text-[10px] text-white/25 tracking-[0.04em]">
            © {new Date().getFullYear()} AgentUS · Vertical Gestão Pública ·
            LGPD compliant
            <span className="mx-2 text-white/10" aria-hidden>·</span>
            Contratação por processo administrativo
          </p>
        </footer>
      </div>

      <Toaster position="top-right" richColors />

      {/* Styles compartilhados com o login privado — mesma linguagem visual */}
      <style>{`
        .gov-login-input {
          width: 100%;
          height: 42px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px;
          padding: 0 13px;
          font-size: 13.5px;
          color: #F1F5F9;
          outline: none;
          transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
        }
        .gov-login-input::placeholder { color: rgba(255,255,255,0.28); }
        .gov-login-input:hover { border-color: rgba(255,255,255,0.20); }
        .gov-login-input:focus {
          background: rgba(255,255,255,0.06);
          border-color: rgba(52,168,83,0.65);
          box-shadow: 0 0 0 3px rgba(52,168,83,0.10);
        }
        .gov-login-input:-webkit-autofill,
        .gov-login-input:-webkit-autofill:hover,
        .gov-login-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px #10141C inset;
          -webkit-text-fill-color: #F1F5F9;
          caret-color: #F1F5F9;
          border-radius: 10px;
        }
        @media (min-width: 640px) {
          .gov-login-input { height: 44px; font-size: 14px; }
        }

        .gov-login-submit {
          margin-top: 18px;
          width: 100%;
          height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          color: #0B0F17;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.005em;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.6),
            0 4px 18px -4px rgba(255,255,255,0.18);
          transition: background-color .25s ease, box-shadow .25s ease, transform .15s ease;
        }
        .gov-login-submit:hover {
          background: #FFFFFF;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.7),
            0 6px 26px -4px rgba(255,255,255,0.30);
        }
        .gov-login-submit:active { background: #E2E8F0; transform: translateY(0.5px); }
        .gov-login-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .gov-login-submit:disabled:hover {
          background: #F8FAFC;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.6),
            0 4px 18px -4px rgba(255,255,255,0.18);
        }

        .gov-view-in {
          animation: govViewIn .38s cubic-bezier(0.21, 1.02, 0.55, 1) both;
        }
        @keyframes govViewIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .gov-view-in { animation: none; }
        }
      `}</style>
    </div>
  );
}

/* ───────────────────────── LOGIN VIEW ───────────────────────── */

function LoginView({ navigate, auth, onRequest }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pré-preenche email se veio via querystring (?email=X) — útil pra links
  // enviados pelo SDR: "Segue seu acesso: <link>?email=demo-gov@..."
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("email");
    if (q) setEmail(q);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // v0.99.8b · Fev/2026 — captura o resultado do login para inspecionar
      // o `tenant.sector` diretamente. Isso é a fonte primária de verdade
      // (o backend sempre devolve tenant no login), e independe de o
      // endpoint `/sector/me` estar disponível na versão da produção.
      const loginRes = await auth.login(email, password);
      // v0.99.8 · Fev/2026 — Gate de exclusividade GOV.
      // A vertical GOV é ISOLADA — tenants privados não podem entrar
      // em /gov/login. Fail-closed: se conseguirmos confirmar setor,
      // valida. Se não conseguirmos (backend antigo, rede lenta),
      // deixamos passar SOMENTE quando o email tem padrão gov claro,
      // pra não travar o operador em ambientes deployados.
      const sectorFromLogin = (
        loginRes?.tenant?.sector
        || auth?.tenant?.sector
        || ""
      ).toString().toLowerCase();
      let isGov = sectorFromLogin.startsWith("gov_");
      if (!isGov) {
        // Sector do login não é GOV. Consulta /sector/me como 2ª fonte,
        // caso o login response tenha sido cortado por uma versão antiga.
        try {
          const me = await api.get("/sector/me");
          const data = me?.data || {};
          const codeLower = (data.code || "").toString().toLowerCase();
          isGov = Boolean(data.is_gov) || codeLower.startsWith("gov_");
        } catch (_e) { /* silencioso — sector desconhecido não é GOV */ }
      }
      if (!isGov) {
        try { await auth.logout?.(); } catch { /* no-op */ }
        toast.error(
          "Este acesso é exclusivo para órgãos cadastrados na vertical GOV. "
          + "Para o painel regular, use /login."
        );
        setLoading(false);
        return;
      }
      toast.success("Bem-vindo!");
      navigate("/gov/painel", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciais inválidas");
    } finally {
      setLoading(false);
    }
  };

  // Detecta credencial demo pra mostrar selo discreto (sem esconder que é demo)
  const isDemo = email.trim().toLowerCase() === "demo-gov@agentus.com.br";

  return (
    <div className="gov-view-in">
      {/* Heading */}
      <div className="text-center mb-6 sm:mb-7">
        <div
          className="text-[9.5px] uppercase tracking-[0.3em] text-white/30 font-medium mb-3 select-none"
          data-testid="gov-login-eyebrow"
        >
          Acesso restrito
        </div>
        <h1 className="font-heading text-[28px] sm:text-[32px] font-semibold tracking-[-0.03em] text-white leading-[1.12]">
          Portal <span className="text-emerald-400">GOV.</span>
        </h1>
        <p className="text-[12.5px] text-white/40 mt-2 tracking-tight max-w-[300px] mx-auto">
          Ambiente exclusivo para órgãos cadastrados
          <br className="hidden sm:inline" /> no AgentUS Gestão Pública.
        </p>
      </div>

      {/* Selo demo (só quando o email é o de demo) */}
      {isDemo && (
        <div
          className="mb-4 flex items-center justify-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/80"
          data-testid="gov-demo-badge"
        >
          <span className="w-4 h-px bg-emerald-400/40" />
          Ambiente demonstrativo · Santa Aurora
          <span className="w-4 h-px bg-emerald-400/40" />
        </div>
      )}

      {/* Form glass */}
      <form
        onSubmit={submit}
        data-testid="gov-login-form"
        className="rounded-[16px] px-5 sm:px-6 pt-5 pb-5 border border-white/[0.08]"
        style={{
          background: "rgba(255,255,255,0.035)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px -20px rgba(0,0,0,0.6)",
        }}
      >
        <div className="space-y-3.5">
          <Field label="Email institucional">
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contato@prefeitura.gov.br"
              data-testid="gov-login-email"
              className="gov-login-input"
            />
          </Field>

          <Field label="Senha">
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="gov-login-password"
                className="gov-login-input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="gov-login-toggle-password"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/35 hover:text-white transition-colors duration-150"
                tabIndex={-1}
                aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPwd ? <EyeSlash size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="gov-login-submit"
          className="gov-login-submit"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full border-[1.5px] border-slate-900/25 border-t-slate-900 animate-spin" />
              Entrando
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 group/btn">
              Acessar portal
              <ArrowRight
                size={13}
                weight="bold"
                className="opacity-60 transition-all duration-300 ease-out group-hover/btn:opacity-100 group-hover/btn:translate-x-0.5"
              />
            </span>
          )}
        </button>
      </form>

      <p className="text-[12px] text-white/40 text-center mt-5 tracking-tight">
        Ainda não possui acesso?{" "}
        <button
          type="button"
          onClick={onRequest}
          data-testid="gov-goto-request"
          className="text-emerald-300 font-semibold hover:underline underline-offset-[5px] decoration-[1.5px] transition-all"
        >
          Solicitar contato
        </button>
      </p>
    </div>
  );
}

/* ───────────────────────── REQUEST VIEW ───────────────────────── */

function RequestView({ onBack }) {
  const empty = {
    org_type: "gov_municipal", org_name: "", cnpj: "",
    contact_name: "", contact_role: "", contact_email: "",
    contact_phone: "", state: "", city: "",
    request_type: "demo", scope_notes: "",
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/gov/leads", form);
      setOk(true);
      toast.success("Solicitação recebida — entraremos em contato em breve.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (ok) {
    return (
      <div className="gov-view-in text-center space-y-4 py-6" data-testid="gov-request-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06]">
          <ArrowRight size={20} weight="bold" className="text-emerald-300" />
        </div>
        <h3 className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-white">
          Solicitação recebida
        </h3>
        <p className="text-[13px] text-white/50 max-w-[320px] mx-auto tracking-tight">
          Nossa equipe entrará em contato em até <strong className="text-white/80">3 dias úteis</strong> com
          os próximos passos institucionais.
        </p>
        <button
          type="button"
          onClick={() => { setForm(empty); setOk(false); }}
          className="text-[12px] text-emerald-300 hover:text-emerald-200 underline underline-offset-4"
          data-testid="gov-request-new"
        >
          Nova solicitação
        </button>
      </div>
    );
  }

  return (
    <div className="gov-view-in">
      {/* Header with back */}
      <div className="flex items-center gap-2 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/80 transition-colors"
          data-testid="gov-back-to-login"
        >
          <ArrowLeft size={12} />
          Voltar ao acesso
        </button>
      </div>

      <div className="text-center mb-6">
        <div className="text-[9.5px] uppercase tracking-[0.3em] text-white/30 font-medium mb-3 select-none">
          Solicitar acesso
        </div>
        <h1 className="font-heading text-[24px] sm:text-[27px] font-semibold tracking-[-0.025em] text-white leading-[1.12]">
          Fale com a equipe <span className="text-emerald-400">GOV.</span>
        </h1>
        <p className="text-[12.5px] text-white/40 mt-2 tracking-tight max-w-[320px] mx-auto">
          Preencha os dados abaixo e nossa equipe entrará em contato para
          apresentar o AgentUS Gestão Pública ao seu órgão.
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid="gov-request-form"
        className="rounded-[16px] px-5 sm:px-6 py-5 border border-white/[0.08] space-y-3.5"
        style={{
          background: "rgba(255,255,255,0.035)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px -20px rgba(0,0,0,0.6)",
        }}
      >
        <Field label="Tipo de órgão">
          <select
            value={form.org_type}
            onChange={(e) => set({ org_type: e.target.value })}
            data-testid="gov-request-org-type"
            className="gov-login-input"
          >
            {ORG_TYPES.map((o) => (
              <option key={o.value} value={o.value} style={{ background: "#10141C" }}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Nome do órgão *">
          <input
            required
            value={form.org_name}
            onChange={(e) => set({ org_name: e.target.value })}
            data-testid="gov-request-org-name"
            placeholder="Ex.: Prefeitura de Osasco"
            className="gov-login-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cidade *">
            <input
              required
              value={form.city}
              onChange={(e) => set({ city: e.target.value })}
              data-testid="gov-request-city"
              className="gov-login-input"
            />
          </Field>
          <Field label="UF *">
            <input
              required
              maxLength={2}
              value={form.state}
              onChange={(e) => set({ state: e.target.value.toUpperCase() })}
              data-testid="gov-request-state"
              className="gov-login-input"
              style={{ textTransform: "uppercase" }}
            />
          </Field>
        </div>

        <Field label="Seu nome *">
          <input
            required
            value={form.contact_name}
            onChange={(e) => set({ contact_name: e.target.value })}
            data-testid="gov-request-contact-name"
            className="gov-login-input"
          />
        </Field>

        <Field label="Cargo / função">
          <input
            value={form.contact_role}
            onChange={(e) => set({ contact_role: e.target.value })}
            data-testid="gov-request-contact-role"
            placeholder="Ex.: Secretário de Administração"
            className="gov-login-input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Email *">
            <input
              required
              type="email"
              value={form.contact_email}
              onChange={(e) => set({ contact_email: e.target.value })}
              data-testid="gov-request-email"
              className="gov-login-input"
            />
          </Field>
          <Field label="Telefone *">
            <input
              required
              value={form.contact_phone}
              onChange={(e) => set({ contact_phone: e.target.value })}
              data-testid="gov-request-phone"
              placeholder="(11) 99999-9999"
              className="gov-login-input"
            />
          </Field>
        </div>

        <Field label="Tipo de solicitação">
          <select
            value={form.request_type}
            onChange={(e) => set({ request_type: e.target.value })}
            data-testid="gov-request-type"
            className="gov-login-input"
          >
            {REQUEST_TYPES.map((r) => (
              <option key={r.value} value={r.value} style={{ background: "#10141C" }}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Contexto (opcional)">
          <textarea
            rows={3}
            maxLength={2000}
            value={form.scope_notes}
            onChange={(e) => set({ scope_notes: e.target.value })}
            data-testid="gov-request-scope"
            placeholder="Nos conte um pouco sobre a realidade do seu órgão."
            className="gov-login-input resize-none"
            style={{ height: "auto", padding: "10px 13px", lineHeight: "1.4" }}
          />
        </Field>

        <button
          type="submit"
          disabled={loading}
          data-testid="gov-request-submit"
          className="gov-login-submit"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full border-[1.5px] border-slate-900/25 border-t-slate-900 animate-spin" />
              Enviando
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 group/btn">
              Enviar solicitação
              <ArrowRight size={13} weight="bold" className="opacity-60 group-hover/btn:opacity-100 group-hover/btn:translate-x-0.5 transition-all duration-300" />
            </span>
          )}
        </button>
      </form>

      <p className="text-[10.5px] text-white/30 text-center mt-4 tracking-tight">
        Sem contratação automática · Sem cobrança em cartão · Atendimento
        exclusivo a órgãos públicos
      </p>
    </div>
  );
}

/* ─────────────────────────── UI HELPERS ─────────────────────────── */

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
